class User {
  /**
   * Constructor to initialize a User model.
   * @param {string} id - Unique identifier for the user.
   * @param {string} username - The username of the user.
   * @param {string} name - Full name of the user.
   * @param {string} email - Email address of the user.
   * @param {string} yearOfStudy - The year of study of the user.
   * @param {string} medicalUniversity - The medical university of the user.
   * @param {string} demographics - Demographic information of the user.
   * @param {boolean} completedQuestionnaire - Whether the questionnaire is completed.
   * @param {Date} createdAt - Timestamp of when the user was created.
   * @param {Date} updatedAt - Timestamp of the last update to the user.
   */
  constructor({
    id,
    username,
    name,
    email,
    yearOfStudy,
    medicalUniversity,
    demographics,
    completedQuestionnaire = false,
    createdAt = new Date(),
    updatedAt = new Date(),
  }) {
    this.id = id;
    this.username = username;
    this.name = name;
    this.email = email;
    this.yearOfStudy = yearOfStudy;
    this.medicalUniversity = medicalUniversity;
    this.demographics = demographics;
    this.completedQuestionnaire = completedQuestionnaire;
    this.createdAt = createdAt;
    this.updatedAt = updatedAt;
  }

  /**
   * Update the user's data.
   * @param {object} updates - Object containing the fields to update.
   */
  update(updates) {
    Object.assign(this, updates);
    this.updatedAt = new Date();
  }

  /**
   * Validate the user data.
   * @returns {boolean} - Returns true if valid, otherwise throws an error.
   */
  validate() {
    if (!this.id) throw new Error('User ID is required.');
    if (!this.username) throw new Error('Username is required.');
    if (!this.name) throw new Error('Name is required.');
    if (!this.email || !/\S+@\S+\.\S+/.test(this.email)) {
      throw new Error('A valid email is required.');
    }
    return true;
  }

  /**
   * Convert the user object to a JSON representation.
   * @returns {object} - JSON representation of the user.
   */
  toJSON() {
    return {
      id: this.id,
      username: this.username,
      name: this.name,
      email: this.email,
      yearOfStudy: this.yearOfStudy,
      medicalUniversity: this.medicalUniversity,
      demographics: this.demographics,
      completedQuestionnaire: this.completedQuestionnaire,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
    };
  }

  /**
   * Create a User instance from a JSON object.
   * @param {object} json - JSON representation of the user.
   * @returns {User} - An instance of the User class.
   */
  static fromJSON(json) {
    return new User(json);
  }

  /**
   * Populate user data from Cognito attributes.
   * @param {object} cognitoUser - CognitoUser object from AWS Amplify Auth.
   * @returns {User} - An instance of the User class populated from Cognito attributes.
   */
  static fromCognitoAttributes(cognitoUser) {
    const { username, attributes } = cognitoUser;
    return new User({
      id: username, // Use Cognito username as ID
      username,
      name: attributes.name,
      email: attributes.email,
      // Add more fields as needed
    });
  }
}

export default User;

